export interface Invoice {
  id: string;
  invoiceMonth: string;
  /** Competência no formato MM-yyyy (API); usada em filtros e dropdowns. */
  competenciaMmYyyy?: string;
  policyNumber: string;
  type: string;
  dueDate: string;
  amount: number;
  status: 'Pago' | 'Pendente' | 'Vencido' | 'Cancelado';
  identSubestipulante?: number;
  codSubestipulante?: number;
  nomeSubestipulante?: string;
  identEndosso?: string;
  codFormaCobranca?: number | null;
  situacaoFatura?: string;
  tipoFaturaCodigo?: string;
}

/** Fields returned by the API (obter-dados-fatura). */
export interface InvoiceDetailApiFields {
  identCobrancaObt: number;
  identSubstipulanteObt: number;
  identEndossoObt: number | null;
  numParcelaObt: number;
  competenciaFaturaObt: string;
  tipoFaturaObt: number;
  nossoNumFatura: string;
  situacaoFaturaObt: string;
  valorImportSegurada: string;
  valorPremioTotalObt: string;
  valorPremioLiquidoObt: string;
  valorIOF: string;
  valorAssistFatura: string;
  valorPagoFatura: string;
  dataIniVigenciaFatObt: string;
  dataFimVigenciaFatObt: string;
  dataEmissaoFatura: string;
  dataCancelFatura: string;
  dataVencFatura: string;
  dataPagFatura: string;
}

export interface InvoiceDetail extends Invoice, InvoiceDetailApiFields {
  insured?: string;
  cnpj?: string;
  issueDate?: string;
  paymentDate?: string;
  paymentMethod?: string;
  observations?: string;
  discounts?: number;
  interest?: number;
  totalAmount: number;
}

export interface PaymentSummary {
  monthlyAmount: number;
  insureds: number;
  nextDueDate: string;
  status: 'Em dia' | 'Atenção' | 'Atrasado';
}

// Invoice list API response (returns card summary + paginated list)
export interface InvoiceListEndorsementDTO {
  idEndosso: string;
}

export interface InvoiceListItemDTO {
  identSubestipulante: number;
  identCobranca: number;
  identEndosso: string;
  identContrato: number;
  nomeSubestipulante: string;
  numParcela: number;
  codFormaCobranca: number | null;
  competenciaFatura: string;
  tipoFatura: string;
  situacaoFatura: string;
  dataIniVigenciaFat: string;
  dataFimVigenciaFat: string;
  valorPremioTotal: number;
  valorPremio: string;
  codSubestipulante: number;
  descTpFatura: string;
  dataEmissao: string;
  dataVencimento: string;
  descFormaCobranca: string;
  codSubestipulanteAgrupador: number | null;
  nomeSubestipulanteAgrupador: string;
  status: string;
  descStatus: string;
  indCancelamentoFatura?: string;
  indFaturaComplementar?: string;
  endossos?: InvoiceListEndorsementDTO[];
}

export interface InvoiceListPageDTO {
  content: InvoiceListItemDTO[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface PaymentInvoiceListResponse {
  mensagem: string;
  possuiPendenciaStatus: boolean;
  proximoVencimento: string;
  valorTotalMesAtual: number;
  pagina: InvoiceListPageDTO;
}

/** Maps invoice list API response to payment summary (cards). */
export const mapPaymentInvoiceListToSummary = (res: PaymentInvoiceListResponse): PaymentSummary => {
  const formatDate = (dateStr: string): string => {
    try {
      const parts = dateStr.split('-').map(Number);
      const [year, month, day] = parts;
      const date = new Date(year, (month || 1) - 1, day || 1);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };
  return {
    monthlyAmount: res.valorTotalMesAtual ?? 0,
    insureds: 0,
    nextDueDate: res.proximoVencimento ? formatDate(res.proximoVencimento) : '',
    status: res.possuiPendenciaStatus ? 'Atenção' : 'Em dia'
  };
};

/** Maps a single invoice list item to Invoice (table row). */
export const mapInvoiceListItemToInvoice = (item: InvoiceListItemDTO): Invoice => {
  const formatCompetencia = (competencia: string): string => {
    try {
      const [month, year] = competencia.split('-');
      const m = parseInt(month, 10);
      const y = parseInt(year, 10);
      const date = new Date(y, m - 1, 1);
      const monthName = date.toLocaleDateString('pt-BR', { month: 'long' });
      return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)}/${y}`;
    } catch {
      return competencia;
    }
  };
  const formatDueDate = (dateStr: string): string => {
    try {
      const part = dateStr.split(' ')[0] || dateStr;
      const [y, m, d] = part.split('-').map(Number);
      const date = new Date(y, m - 1, d);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };
  const amount = typeof item.valorPremioTotal === 'number' ? item.valorPremioTotal : parseFloat(String(item.valorPremio || '0')) || 0;
  return {
    id: String(item.identCobranca),
    invoiceMonth: formatCompetencia(item.competenciaFatura),
    competenciaMmYyyy: item.competenciaFatura,
    policyNumber: String(item.identContrato),
    type: item.descTpFatura || item.tipoFatura || '',
    dueDate: formatDueDate(item.dataVencimento),
    amount,
    status: (item.descStatus || item.status || '') as Invoice['status'],
    identSubestipulante: item.identSubestipulante,
    codSubestipulante: item.codSubestipulante,
    nomeSubestipulante: item.nomeSubestipulante,
    identEndosso: item.identEndosso,
    codFormaCobranca: item.codFormaCobranca,
    situacaoFatura: item.situacaoFatura,
    tipoFaturaCodigo: item.tipoFatura,
  };
};

/** Item returned by obter-competencias-faturadas. */
export interface PaymentInvoiceCompetenceItemDTO {
  identificadorSubestipulante: number;
  identContrato: number;
  identCobranca: number;
  identEndosso: number;
  competenciaFatura: string;
}

/** Response from /v1/bff/vida/segurado/obter-competencias-faturadas. */
export interface PaymentInvoiceCompetenceResponse {
  retorno: PaymentInvoiceCompetenceItemDTO[];
  mensagem: string;
}

/** Parameters for fetching billed competences. */
export interface PaymentInvoiceCompetenceParams {
  identContrato: number;
  identificadorSubestipulante?: number;
}

/** Parameters for fetching invoice list. */
export interface PaymentInvoiceListParams {
  identContrato: number; // Required: Contract identifier
  competenciaFatura: string; // Required: Invoice period (MM-yyyy format, e.g., "02-2018")
  identSubestipulante?: number; // Optional: Sub-insurer identifier
  tipoFatura?: string; // Optional: Invoice type
  idEndosso?: number; // Optional: Endorsement identifier
  codFormaCobranca?: number; // Optional: Payment method code
  origemFront?: string; // Optional: Front origin
  pSitFatura?: string; // Optional: Invoice status (e.g., "B")
  page?: number; // Optional: Page number (0-based). Default: 0
  size?: number; // Optional: Page size. Default: 6
}

export interface UseVidaPaymentReturn {
  summary: PaymentSummary;
  invoices: Invoice[];
  isLoading: boolean;
  selectedInvoice: InvoiceDetail | null;
  selectInvoice: (invoice: Invoice) => Promise<void>;
  setSelectedInvoice: (invoice: InvoiceDetail | null) => void;
  closeModal: () => void;
  getPaymentInvoiceHistory: (
    params: PaymentInvoiceListParams
  ) => Promise<PaymentInvoiceListResponse>;
  getPaymentInvoiceHistoryByCompetencias: (
    params: Omit<PaymentInvoiceListParams, 'competenciaFatura'> & { competenciasFatura: string[] }
  ) => Promise<PaymentInvoiceListResponse[]>;
  /** Load all competences from API for the dropdown. */
  loadPaymentInvoiceCompetencias: (
    params: PaymentInvoiceCompetenceParams
  ) => Promise<PaymentInvoiceCompetenceItemDTO[]>;
  /** Competences (MM-yyyy) filtered to the last 4 months (initial load of invoices). */
  getPaymentInvoiceCompetenciaFaturas: (
    params: PaymentInvoiceCompetenceParams
  ) => Promise<string[]>;
  /** Filter dropdown options by subestipulante (undefined = all). */
  applyCompetenciaSubestipulanteFilter: (identificadorSubestipulante?: number) => void;
  competenciaDropdownOptions: Array<{ label: string; value: string }>;
  // Pagination state
  currentPage: number;
  totalPages: number;
  totalElements: number;
  pageSize: number;
}

// API Response Types
export interface PaymentListResponse {
  success: boolean;
  message?: string;
  data?: Invoice[];
  totalElements?: number;
}

// Payment Invoice History DTO (matches backend PaymentInvoiceHistoryDTO)
export interface PaymentInvoiceHistoryDTO {
  id: number;
  companyId: string;
  contractNumber: string;
  billingMonth: string; // LocalDate from backend (yyyy-MM-dd)
  type: string;
  emissionDate: string; // LocalDate from backend (yyyy-MM-dd)
  dueDate: string; // LocalDate from backend (yyyy-MM-dd)
  totalValue: number; // BigDecimal from backend
  amount: number; // BigDecimal from backend
  status: string;
}

// Page Response (matches backend PageResponse<T>)
export interface PageResponse<T> {
  content: T[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

// Payment Invoice History Response (PageResponse<PaymentInvoiceHistoryDTO>)
export type PaymentInvoiceHistoryResponse = PageResponse<PaymentInvoiceHistoryDTO>;

// Invoice detail API response (obter-dados-fatura returns paginated content)
export interface InvoiceDetailItemDTO {
  identCobrancaObt: number;
  identSubstipulanteObt: number;
  identEndossoObt: number | null;
  numParcelaObt: number;
  competenciaFaturaObt: string; // MM-yyyy
  tipoFaturaObt: number;
  nossoNumFatura: string;
  situacaoFaturaObt: string;
  valorImportSegurada: string;
  valorPremioTotalObt: string;
  valorPremioLiquidoObt: string;
  valorIOF: string;
  valorAssistFatura: string;
  valorPagoFatura: string;
  dataIniVigenciaFatObt: string; // dd-MM-yyyy
  dataFimVigenciaFatObt: string;
  dataEmissaoFatura: string; // dd-MM-yyyy
  dataCancelFatura: string;
  dataVencFatura: string; // dd-MM-yyyy
  dataPagFatura: string; // dd-MM-yyyy
}

export interface InvoiceDetailPageDTO {
  content: InvoiceDetailItemDTO[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface PaymentInvoiceDetailResponse {
  pagina: InvoiceDetailPageDTO;
}

// Helper function to map PaymentInvoiceHistoryDTO to Invoice
export const mapPaymentInvoiceHistoryToInvoice = (dto: PaymentInvoiceHistoryDTO): Invoice => {
  // Format billingMonth from yyyy-MM-dd to "Month/Year" format
  const formatBillingMonth = (dateStr: string): string => {
    try {
      // Parse date as local date (not UTC) to avoid timezone issues
      // "2025-01-01" should be parsed as January 1, 2025, not December 31, 2024
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
      const monthName = date.toLocaleDateString('pt-BR', { month: 'long' });
      return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)}/${year}`;
    } catch {
      return dateStr;
    }
  };

  // Format dueDate from yyyy-MM-dd to dd/MM/yyyy
  const formatDueDate = (dateStr: string): string => {
    try {
      // Parse date as local date (not UTC) to avoid timezone issues
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  let competenciaMmYyyy: string | undefined;
  try {
    const [year, month] = dto.billingMonth.split('-').map(Number);
    if (year && month) {
      competenciaMmYyyy = `${String(month).padStart(2, '0')}-${year}`;
    }
  } catch {
    competenciaMmYyyy = undefined;
  }

  return {
    id: dto.id.toString(),
    invoiceMonth: formatBillingMonth(dto.billingMonth),
    competenciaMmYyyy,
    policyNumber: dto.contractNumber,
    type: dto.type,
    dueDate: formatDueDate(dto.dueDate),
    amount: dto.amount,
    status: dto.status as Invoice['status']
  };
};

/** Formats date string dd-MM-yyyy to dd/MM/yyyy. */
const formatDateDdMmYyyy = (dateStr: string | null): string => {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length >= 3) {
      const [d, m, y] = parts;
      return `${d}/${m}/${y}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
};

/** Formats competencia MM-yyyy to "Month/Year" (e.g. "09-2020" -> "Setembro/2020"). */
const formatCompetenciaToMonthYear = (competencia: string): string => {
  try {
    const [month, year] = competencia.split('-');
    const m = parseInt(month, 10);
    const y = parseInt(year, 10);
    const date = new Date(y, m - 1, 1);
    const monthName = date.toLocaleDateString('pt-BR', { month: 'long' });
    return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)}/${y}`;
  } catch {
    return competencia;
  }
};

/** Maps situacaoFaturaObt to display status. */
const mapSituacaoToStatus = (situacao: string): Invoice['status'] => {
  const s = situacao?.toUpperCase();
  if (s === 'E') return 'Pendente'; // Em processamento
  if (s === 'P') return 'Pendente';
  if (s === 'B' || s === 'G') return 'Pago';
  if (s === 'V') return 'Vencido';
  if (s === 'C') return 'Cancelado';
  return 'Pendente';
};

/** Maps a single invoice detail item (obter-dados-fatura content) to InvoiceDetail. */
export const mapInvoiceDetailItemToInvoiceDetail = (
  item: InvoiceDetailItemDTO,
  policyNumber: string = ''
): InvoiceDetail => {
  const amount = parseFloat(item.valorPremioTotalObt || '0') || 0;
  const totalAmount = parseFloat(item.valorPagoFatura || item.valorPremioTotalObt || '0') || 0;

  const interest = parseFloat(item.valorIOF || '0') || 0;

  return {
    id: String(item.identCobrancaObt),
    invoiceMonth: formatCompetenciaToMonthYear(item.competenciaFaturaObt),
    policyNumber: policyNumber || String(item.identCobrancaObt),
    type: String(item.tipoFaturaObt),
    dueDate: formatDateDdMmYyyy(item.dataVencFatura),
    amount,
    status: mapSituacaoToStatus(item.situacaoFaturaObt),
    totalAmount,
    issueDate: formatDateDdMmYyyy(item.dataEmissaoFatura),
    paymentDate: formatDateDdMmYyyy(item.dataPagFatura),
    interest,
    // Fields returned by the API (obter-dados-fatura)
    identCobrancaObt: item.identCobrancaObt,
    identSubstipulanteObt: item.identSubstipulanteObt,
    identEndossoObt: item.identEndossoObt,
    numParcelaObt: item.numParcelaObt,
    competenciaFaturaObt: item.competenciaFaturaObt,
    tipoFaturaObt: item.tipoFaturaObt,
    nossoNumFatura: item.nossoNumFatura ?? '',
    situacaoFaturaObt: item.situacaoFaturaObt,
    valorImportSegurada: item.valorImportSegurada ?? '',
    valorPremioTotalObt: item.valorPremioTotalObt ?? '',
    valorPremioLiquidoObt: item.valorPremioLiquidoObt ?? '',
    valorIOF: item.valorIOF ?? '',
    valorAssistFatura: item.valorAssistFatura ?? '',
    valorPagoFatura: item.valorPagoFatura ?? '',
    dataIniVigenciaFatObt: item.dataIniVigenciaFatObt ?? '',
    dataFimVigenciaFatObt: item.dataFimVigenciaFatObt ?? '',
    dataEmissaoFatura: item.dataEmissaoFatura ?? '',
    dataCancelFatura: item.dataCancelFatura ?? '',
    dataVencFatura: item.dataVencFatura ?? '',
    dataPagFatura: item.dataPagFatura ?? '',
  };
};



